import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import ScrollToTop from "@/components/common/ScrollToTop";
import SiteHeader from "@/components/layout/SiteHeader";
import SiteFooter from "@/components/layout/SiteFooter";
import PageNameBar from "@/components/layout/PageNameBar";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import CaseStudyDetailPage from "@/pages/CaseStudyDetailPage";
import PapersPage from "@/pages/PapersPage";
import BlogsPage from "@/pages/BlogsPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import ContactPage from "@/pages/ContactPage";
import GalleryPage from "@/pages/GalleryPage";
import SafetyMeasuresPage from "@/pages/SafetyMeasuresPage";
import TechnologyPage from "@/pages/TechnologyPage";
import HighlightsPage from "@/pages/HighlightsPage";
import OurTeamPage from "@/pages/OurTeamPage";
import AestheticDentistryPage from "@/pages/AestheticDentistryPage";
import ImplantDentistryPage from "@/pages/ImplantDentistryPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import IntroLoader from "./components/common/IntroLoader";
import PageLoader from "./components/common/PageLoader";

function PublicSiteRoutes() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const prevPath = useRef(null);

  useEffect(() => {
    if (prevPath.current === null) {
      prevPath.current = location.pathname;
      return;
    }

    if (prevPath.current === location.pathname) return;

    setLoading(true);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 600);

    prevPath.current = location.pathname;

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#ffffff]">
      {loading && <PageLoader />}

      <SiteHeader />
      <PageNameBar />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/case-studies" element={<CaseStudiesPage />} />
        <Route path="/case-studies/:caseId" element={<CaseStudyDetailPage />} />
        <Route path="/dentistry/aesthetic" element={<AestheticDentistryPage />} />
        <Route path="/dentistry/implant" element={<ImplantDentistryPage />} />
        <Route path="/safety-measures" element={<SafetyMeasuresPage />} />
        <Route path="/technology" element={<TechnologyPage />} />
        <Route path="/highlights" element={<HighlightsPage />} />
        <Route path="/our-team" element={<OurTeamPage />} />
        <Route path="/published-papers" element={<PapersPage />} />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/blogs/:blogId" element={<BlogDetailPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <SiteFooter />
      <WhatsAppButton />
    </div>
  );
}

function App() {

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hasSeenLoader = sessionStorage.getItem("seenLoader");

    if (hasSeenLoader) {
      setLoading(false);
    }
  }, []);

  const handleFinish = () => {
    sessionStorage.setItem("seenLoader", "true");
    setLoading(false);
  };

  if (loading) {
    return <IntroLoader onFinish={handleFinish} />;
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      
      <Toaster richColors position="top-right" />
      <Routes>
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/*" element={<PublicSiteRoutes skipFirstLoad={true} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
